/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { VehicleType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AgencyLocationProps {
  id: string;
  name: string;
  emailAddress: string | null;
  primaryPhoneNumber: string | null;
  secondaryPhoneNumber: string | null;
  smsPhoneNumber: string | null;
  faxPhoneNumber: string | null;
  notes: string | null;
  vehicleType: VehicleType | null;
  estimatedDepartureTimeInMinutes: number | null;
  isInTransportNet: boolean;
  locationId: string | null;
  agencyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class AgencyLocationRecord extends Record<AgencyLocationProps>({
  id: null as any,
  name: null as any,
  emailAddress: null as any,
  primaryPhoneNumber: null as any,
  secondaryPhoneNumber: null as any,
  smsPhoneNumber: null as any,
  faxPhoneNumber: null as any,
  notes: null as any,
  vehicleType: null as any,
  estimatedDepartureTimeInMinutes: null as any,
  isInTransportNet: null as any,
  locationId: null as any,
  agencyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    emailAddress: string | null;
    primaryPhoneNumber: string | null;
    secondaryPhoneNumber: string | null;
    smsPhoneNumber: string | null;
    faxPhoneNumber: string | null;
    notes: string | null;
    vehicleType: VehicleType | null;
    estimatedDepartureTimeInMinutes: number | null;
    isInTransportNet: boolean;
    locationId: string | null;
    agencyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): AgencyLocationRecord {
    if (json == null) {
      throw new Error('AgencyLocationRecord.fromJS: parameter cannot be null');
    }
    return new AgencyLocationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'AgencyLocationJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'AgencyLocationJson.name'),
      emailAddress: json.emailAddress,
      primaryPhoneNumber: json.primaryPhoneNumber,
      secondaryPhoneNumber: json.secondaryPhoneNumber,
      smsPhoneNumber: json.smsPhoneNumber,
      faxPhoneNumber: json.faxPhoneNumber,
      notes: json.notes,
      vehicleType: json.vehicleType,
      estimatedDepartureTimeInMinutes: json.estimatedDepartureTimeInMinutes,
      isInTransportNet: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isInTransportNet', false),
        'AgencyLocationJson.isInTransportNet',
      ),
      locationId: json.locationId,
      agencyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'agencyId', '00000000-0000-0000-0000-000000000000'),
        'AgencyLocationJson.agencyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    emailAddress: string | null;
    primaryPhoneNumber: string | null;
    secondaryPhoneNumber: string | null;
    smsPhoneNumber: string | null;
    faxPhoneNumber: string | null;
    notes: string | null;
    vehicleType: VehicleType | null;
    estimatedDepartureTimeInMinutes: number | null;
    isInTransportNet: boolean;
    locationId: string | null;
    agencyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [AgencyLocationRecord, Error[]] {
    if (json == null) {
      throw new Error('AgencyLocationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AgencyLocationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'AgencyLocationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'AgencyLocationJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      emailAddress: tryOrDefault(
        () => json.emailAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primaryPhoneNumber: tryOrDefault(
        () => json.primaryPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      secondaryPhoneNumber: tryOrDefault(
        () => json.secondaryPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      smsPhoneNumber: tryOrDefault(
        () => json.smsPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      faxPhoneNumber: tryOrDefault(
        () => json.faxPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleType: tryOrDefault(
        () => json.vehicleType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedDepartureTimeInMinutes: tryOrDefault(
        () => json.estimatedDepartureTimeInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isInTransportNet: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isInTransportNet', false),
            'AgencyLocationJson.isInTransportNet',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'agencyId', '00000000-0000-0000-0000-000000000000'),
            'AgencyLocationJson.agencyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: AgencyLocationProps) {
    super(props);
    if (props == null) {
      throw new Error('AgencyLocationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AgencyLocationViewModelBase implements AgencyLocationProps {
  protected readonly _props: AgencyLocationProps;
  private _json?: AgencyLocationProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get emailAddress(): string | null {
    return this._props.emailAddress;
  }
  get primaryPhoneNumber(): string | null {
    return this._props.primaryPhoneNumber;
  }
  get secondaryPhoneNumber(): string | null {
    return this._props.secondaryPhoneNumber;
  }
  get smsPhoneNumber(): string | null {
    return this._props.smsPhoneNumber;
  }
  get faxPhoneNumber(): string | null {
    return this._props.faxPhoneNumber;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get vehicleType(): VehicleType | null {
    return this._props.vehicleType;
  }
  get estimatedDepartureTimeInMinutes(): number | null {
    return this._props.estimatedDepartureTimeInMinutes;
  }
  get isInTransportNet(): boolean {
    return this._props.isInTransportNet;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }
  get agencyId(): string {
    return this._props.agencyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAir() {
    return this._props.vehicleType === VehicleType.Air;
  }
  public get isFixedWing() {
    return this._props.vehicleType === VehicleType.FixedWing;
  }
  public get isGround() {
    return this._props.vehicleType === VehicleType.Ground;
  }
  public get isRotorWing() {
    return this._props.vehicleType === VehicleType.RotorWing;
  }
  public get isUnspecified() {
    return this._props.vehicleType === VehicleType.Unspecified;
  }

  public isAnyVehicleType(...vehicleType: VehicleType[]) {
    return this._props.vehicleType != null && vehicleType.includes(this._props.vehicleType);
  }

  constructor(props: AgencyLocationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        emailAddress: this._props.emailAddress,
        primaryPhoneNumber: this._props.primaryPhoneNumber,
        secondaryPhoneNumber: this._props.secondaryPhoneNumber,
        smsPhoneNumber: this._props.smsPhoneNumber,
        faxPhoneNumber: this._props.faxPhoneNumber,
        notes: this._props.notes,
        vehicleType: this._props.vehicleType,
        estimatedDepartureTimeInMinutes: this._props.estimatedDepartureTimeInMinutes,
        isInTransportNet: this._props.isInTransportNet,
        locationId: this._props.locationId,
        agencyId: this._props.agencyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllAgencyLocationEntities = (state: AppState) =>
  state.entityStores.agencyLocation.entities;

export const selectAgencyLocationById = (id: string) => (state: AppState) =>
  state.entityStores.agencyLocation.entities.get(id);

export const selectAgencyLocationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.agencyLocation.meta.get(id);

export const selectAgencyLocationAndMetadataById = (id: string) =>
  createSelector(
    selectAgencyLocationById(id),
    selectAgencyLocationMetadataById(id),
    (agencyLocation, meta) => ({ agencyLocation, meta }),
  );

export function selectAgencyLocationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllAgencyLocationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, AgencyLocationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectAgencyLocationEntitiesByName(name: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByEmailAddress(emailAddress: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.emailAddress === emailAddress).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByPrimaryPhoneNumber(primaryPhoneNumber: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.primaryPhoneNumber === primaryPhoneNumber).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesBySecondaryPhoneNumber(secondaryPhoneNumber: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.secondaryPhoneNumber === secondaryPhoneNumber).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesBySmsPhoneNumber(smsPhoneNumber: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.smsPhoneNumber === smsPhoneNumber).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByFaxPhoneNumber(faxPhoneNumber: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.faxPhoneNumber === faxPhoneNumber).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByNotes(notes: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByVehicleType(vehicleType: VehicleType) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.vehicleType === vehicleType).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByEstimatedDepartureTimeInMinutes(
  estimatedDepartureTimeInMinutes: number,
) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities
      .filter((e) => e.estimatedDepartureTimeInMinutes === estimatedDepartureTimeInMinutes)
      .valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByIsInTransportNet(isInTransportNet: boolean) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.isInTransportNet === isInTransportNet).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectAgencyLocationEntitiesByAgencyId(agencyId: string) {
  return createSelector(selectAllAgencyLocationEntities, (entities) =>
    entities.filter((e) => e.agencyId === agencyId).valueSeq(),
  );
}
//#endregion Selectors
